/* eslint-disable max-lines-per-function */
/* eslint-disable max-len */
/* eslint-disable no-unused-vars */
/* eslint-disable id-length */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { AutoComplete } from 'primereact/autocomplete';
import { Calendar } from 'primereact/calendar';
import './addEditDialog.scss';
import Loader from '../../Components/Loader';

const OPTION_TYPES = {
  CLIENT: 'zoho-client-names',
  STATUS: 'zoho-invoice-status',
};

const LIMITS = {
  INVOICE_NUMBER_MAX_LENGTH: 50,
  AMOUNT_MIN: -999999999.99,
  AMOUNT_MAX: 999999999.99,
  comment_MAX_LENGTH: 500,
  DATE_MIN: new Date('2000-01-01'),
  DATE_MAX: new Date('2099-12-31'),
};

const AddEditDialog = ({
  open,
  onClose,
  baseURL,
  headers,
  onSuccess,
  mode = 'add',
  initialData = {},
}) => {
  const [form, setForm] = useState({
    zohoInvoiceNumber: '',
    zohoClientId: '',
    zohoBasicInvoiceAmount: '',
    zohoInvoiceAmountWithTax: '',
    invoiceDate: '',
    zohoInvoiceStatusId: '',
    comment: '',
  });

  // AutoComplete stores the full selected option object { label, value }
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);

  // Filtered suggestion lists
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [statusSuggestions, setStatusSuggestions] = useState([]);

  const [options, setOptions] = useState({ clientNames: [], invoiceStatuses: [] });
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [originalInvoiceNumber, setOriginalInvoiceNumber] = useState('');
  const [dialogMode, setDialogMode] = useState(mode);

  const findOptionByValue = (opts, value) => opts.find(o => o.value === value) || null;
  const findOptionByLabel = (opts, label) => opts.find(o => o.label === label) || null;

  // Update dialog mode when dialog opens with new mode
  useEffect(() => {
    if (open) {
      setDialogMode(mode);
    }
  }, [open, mode]);

  // Populate form in edit mode after options load
  useEffect(() => {
    if (dialogMode === 'edit' && initialData && options.clientNames.length && options.invoiceStatuses.length) {
      const clientOpt
        = findOptionByValue(options.clientNames, initialData.zohoClientId)
        || findOptionByLabel(options.clientNames, initialData.zohoClientName);

      const statusOpt
        = findOptionByValue(options.invoiceStatuses, initialData.zohoInvoiceStatusId)
        || findOptionByLabel(options.invoiceStatuses, initialData.zohoInvoiceStatus);

      setSelectedClient(clientOpt || null);
      setSelectedStatus(statusOpt || null);

      const invoiceNumber = initialData.zohoInvoiceNumber || '';
      setOriginalInvoiceNumber(invoiceNumber);

      setForm({
        zohoInvoiceNumber: invoiceNumber,
        zohoClientId: clientOpt?.value || '',
        zohoBasicInvoiceAmount: initialData.zohoBasicInvoiceAmount !== undefined
          ? parseFloat(String(initialData.zohoBasicInvoiceAmount).replace(/,/g, ''))
          : '',
        zohoInvoiceAmountWithTax: initialData.zohoInvoiceAmountWithTax !== undefined
          ? parseFloat(String(initialData.zohoInvoiceAmountWithTax).replace(/,/g, ''))
          : '',
        invoiceDate: initialData.invoiceDate?.split('T')[0] || initialData.zohoInvoiceDate || '',
        zohoInvoiceStatusId: statusOpt?.value || '',
        comment: initialData.comment === '-' ? '' : (initialData.comment || ''),
      });
    }
  }, [dialogMode, initialData, options.clientNames, options.invoiceStatuses]);

  // Set default status to "Pending to Collect" for add mode
  useEffect(() => {
    if (dialogMode === 'add' && options.invoiceStatuses.length > 0) {
      const defaultStatus = findOptionByValue(options.invoiceStatuses, 1); // Pending to Collect
      if (defaultStatus && !form.zohoInvoiceStatusId) {
        setSelectedStatus(defaultStatus);
        setForm(prev => ({ ...prev, zohoInvoiceStatusId: defaultStatus.value }));
      }
    }
  }, [dialogMode, options.invoiceStatuses]);

  // Reset selections when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedClient(null);
      setSelectedStatus(null);
      setOriginalInvoiceNumber('');
      setForm({
        zohoInvoiceNumber: '',
        zohoClientId: '',
        zohoBasicInvoiceAmount: '',
        zohoInvoiceAmountWithTax: '',
        invoiceDate: '',
        zohoInvoiceStatusId: '',
        comment: '',
      });
    }
  }, [open]);

  const fetchOptions = useCallback(async (type) => {
    try {
      const res = await fetch(`${baseURL}/options/${type}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
      });
      const data = await res.json();
      return data?.data || [];
    } catch {
      return [];
    }
  }, [baseURL, headers]);

  useEffect(() => {
    if (!open) return;
    setLoadingOptions(true);
    Promise.all([
      fetchOptions(OPTION_TYPES.CLIENT),
      fetchOptions(OPTION_TYPES.STATUS),
    ]).then(([clients, statuses]) => {
      setOptions({ clientNames: clients, invoiceStatuses: statuses });
      setLoadingOptions(false);
    });
  }, [open, fetchOptions]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  // AutoComplete search handlers — filter from already-loaded options
  const searchClients = (event) => {
    const query = event.query?.toLowerCase() || '';
    setClientSuggestions(
      options.clientNames.filter(o => o.label?.toLowerCase().includes(query)),
    );
  };

  const searchStatuses = (event) => {
    const query = event.query?.toLowerCase() || '';
    setStatusSuggestions(
      options.invoiceStatuses.filter(o => o.label?.toLowerCase().includes(query)),
    );
  };

  // When user selects a suggestion or clears
  const handleClientSelect = (e) => {
    const opt = e.value; // { label, value } object
    setSelectedClient(opt || null);
    setForm(prev => ({ ...prev, zohoClientId: opt?.value || '' }));
  };

  const handleStatusSelect = (e) => {
    const opt = e.value;
    setSelectedStatus(opt || null);
    setForm(prev => ({ ...prev, zohoInvoiceStatusId: opt?.value || '' }));
  };

  // If user clears the text manually (types but doesn't pick), clear the stored id
  const handleClientChange = (e) => {
    if (!e.value || typeof e.value === 'string') {
      setSelectedClient(e.value || null);
      setForm(prev => ({ ...prev, zohoClientId: '' }));
    }
  };

  const handleStatusChange = (e) => {
    if (!e.value || typeof e.value === 'string') {
      setSelectedStatus(e.value || null);
      setForm(prev => ({ ...prev, zohoInvoiceStatusId: '' }));
    }
  };

  const checkDuplicateInvoiceNumber = async (invoiceNumber) => {
    try {
      const res = await fetch(`${baseURL}/check-duplicate-invoice-number`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ zohoInvoiceNumber: invoiceNumber }),
      });
      const data = await res.json();
      return data?.isDuplicate || false;
    } catch {
      toast.error('Failed to check duplicate invoice number');
      return true; // Assume duplicate on error to prevent submission
    }
  };

  const validate = () => {
    const e = {};
    if (!form.zohoInvoiceNumber) e.zohoInvoiceNumber = 'Required';
    else if (form.zohoInvoiceNumber.length > LIMITS.INVOICE_NUMBER_MAX_LENGTH) {
      e.zohoInvoiceNumber = `Max ${LIMITS.INVOICE_NUMBER_MAX_LENGTH} characters`;
    }

    if (!form.zohoClientId) e.zohoClientId = 'Client is required';

    const basicAmt = form.zohoBasicInvoiceAmount !== '' ? parseFloat(form.zohoBasicInvoiceAmount) : NaN;
    if (isNaN(basicAmt)) e.zohoBasicInvoiceAmount = 'Required';
    else if (basicAmt < LIMITS.AMOUNT_MIN) {
      e.zohoBasicInvoiceAmount = `Must be at least ${LIMITS.AMOUNT_MIN}`;
    } else if (basicAmt > LIMITS.AMOUNT_MAX) {
      e.zohoBasicInvoiceAmount = `Cannot exceed ${LIMITS.AMOUNT_MAX}`;
    }

    const taxAmt = form.zohoInvoiceAmountWithTax !== '' ? parseFloat(form.zohoInvoiceAmountWithTax) : NaN;
    if (isNaN(taxAmt)) e.zohoInvoiceAmountWithTax = 'Required';
    else if (taxAmt < LIMITS.AMOUNT_MIN) {
      e.zohoInvoiceAmountWithTax = `Must be at least ${LIMITS.AMOUNT_MIN}`;
    } else if (taxAmt > LIMITS.AMOUNT_MAX) {
      e.zohoInvoiceAmountWithTax = `Cannot exceed ${LIMITS.AMOUNT_MAX}`;
    }

    if (!form.invoiceDate) e.invoiceDate = 'Required';
    else {
      const invoiceDate = new Date(form.invoiceDate);
      if (invoiceDate < LIMITS.DATE_MIN || invoiceDate > LIMITS.DATE_MAX) {
        e.invoiceDate = 'Invalid date range';
      }
    }

    if (!form.zohoInvoiceStatusId) e.zohoInvoiceStatusId = 'Status is required';

    if (form.comment && form.comment.length > LIMITS.comment_MAX_LENGTH) {
      e.comment = `Max ${LIMITS.comment_MAX_LENGTH} characters`;
    }

    return e;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (Object.keys(err).length > 0) {
      toast.error(Object.values(err)[0]);
      return;
    }

    // Check for duplicate invoice number if in edit mode and number has changed
    if (dialogMode === 'edit' && form.zohoInvoiceNumber !== originalInvoiceNumber) {
      const isDuplicate = await checkDuplicateInvoiceNumber(form.zohoInvoiceNumber);
      if (isDuplicate) {
        toast.error('Zoho Invoice number already exists. Please use a different zoho invoice number.');
        return;
      }
    }

    setSubmitting(true);
    try {
      let endpoint = `${baseURL}/create-zoho-invoice`;
      let method = 'POST';

      if (dialogMode === 'edit') {
        const invoiceId = initialData.id || initialData.invoiceId || initialData.zohoInvoiceId;
        endpoint = `${baseURL}/update-zoho-invoice/${invoiceId}`;
        method = 'PUT';
      }

      // Format payload correctly for the backend
      const payload = {
        ...form,
        zohoBasicInvoiceAmount: parseFloat(form.zohoBasicInvoiceAmount),
        zohoInvoiceAmountWithTax: parseFloat(form.zohoInvoiceAmountWithTax),
      };

      const res = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok) {
        toast.success(`Invoice ${dialogMode === 'edit' ? 'updated' : 'created'} successfully!`);
        onSuccess?.(data);
        onClose();
      } else {
        toast.error(data?.message || `Failed to ${dialogMode === 'edit' ? 'update' : 'create'} invoice.`);
      }
    } catch {
      toast.error(`Failed to ${dialogMode === 'edit' ? 'update' : 'create'} invoice.`);
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div>
      <Button label="Cancel" onClick={onClose} className="p-button-text" />
      <Button
        className={dialogMode === 'edit' ? 'p-button-edit' : 'p-button-add'}
        label={dialogMode === 'edit' ? 'Update' : 'Add'}
        onClick={handleSubmit}
        loading={submitting}
      />
    </div>
  );

  return (
    <Dialog
      className="zoho-invoice-dialog"
      header={dialogMode === 'edit' ? 'Edit Zoho Invoice' : 'Add Zoho Invoice'}
      visible={open}
      style={{ width: '50vw' }}
      onHide={onClose}
      footer={footer}
      modal
    >
      {loadingOptions ? (
        <Loader text="Loading options..." />
      ) : (
        <div className="zoho-invoice-form-grid">

          {/* Invoice Number */}
          <div className="form-field">
            <label>Zoho Invoice Number <span className="required-asterisk">*</span></label>
            <InputText
              value={form.zohoInvoiceNumber}
              onChange={(e) => handleChange('zohoInvoiceNumber')(e)}
              maxLength={LIMITS.INVOICE_NUMBER_MAX_LENGTH}
              placeholder="Enter Zoho Invoice Number"
            />
          </div>

          {/* Client AutoComplete */}
          <div className="form-field">
            <label>Zoho Client Name <span className="required-asterisk">*</span></label>
            <AutoComplete
              value={selectedClient}
              suggestions={clientSuggestions}
              completeMethod={searchClients}
              field="label"
              dropdown
              forceSelection
              onChange={handleClientChange}
              onSelect={handleClientSelect}
              onClear={() => {
                setSelectedClient(null); setForm(p => ({ ...p, zohoClientId: '' }));
              }}
              placeholder="Select Client"
              className="zoho-autocomplete"
              panelClassName="zoho-autocomplete-panel"
            />
          </div>

          {/* Basic Amount */}
          <div className="form-field">
            <label>Zoho Basic Invoice Amount (INR) <span className="required-asterisk">*</span></label>
            <InputText
              type="number"
              step="0.01"
              min={LIMITS.AMOUNT_MIN}
              max={LIMITS.AMOUNT_MAX}
              value={form.zohoBasicInvoiceAmount}
              onChange={handleChange('zohoBasicInvoiceAmount')}
              placeholder="Enter Zoho Basic Invoice Amount"
            />
          </div>

          {/* Amount With Tax */}
          <div className="form-field">
            <label>Zoho Invoice Amount With Tax (INR) <span className="required-asterisk">*</span></label>
            <InputText
              type="number"
              step="0.01"
              min={LIMITS.AMOUNT_MIN}
              max={LIMITS.AMOUNT_MAX}
              value={form.zohoInvoiceAmountWithTax}
              onChange={handleChange('zohoInvoiceAmountWithTax')}
              placeholder="Enter Zoho Invoice Amount With Tax"
            />
          </div>

          {/* Date */}
          <div className="form-field">
            <label>Invoice Date <span className="required-asterisk">*</span></label>
            <Calendar
              value={form.invoiceDate ? new Date(form.invoiceDate) : null}
              onChange={(e) => {
                const date = e.value;
                const formattedDate = date
                  ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
                  : '';
                handleChange('invoiceDate')({
                  target: { value: formattedDate },
                });
              }}
              dateFormat="yy-mm-dd"
              showIcon
              minDate={LIMITS.DATE_MIN}
              maxDate={LIMITS.DATE_MAX}
              panelClassName="zoho-datepicker"
              placeholder="Choose Date"
            />
          </div>

          {/* Status AutoComplete */}
          <div className="form-field">
            <label>Zoho Invoice Status <span className="required-asterisk">*</span></label>
            <AutoComplete
              value={selectedStatus}
              suggestions={statusSuggestions}
              completeMethod={searchStatuses}
              field="label"
              dropdown
              forceSelection
              onChange={handleStatusChange}
              onSelect={handleStatusSelect}
              onClear={() => {
                setSelectedStatus(null); setForm(p => ({ ...p, zohoInvoiceStatusId: '' }));
              }}
              placeholder="Select Status"
              className="zoho-autocomplete"
              panelClassName="zoho-autocomplete-panel"
            />
          </div>

          {/* comment */}
          <div className="form-field full-width">
            <label>Comment</label>
            <InputTextarea
              value={form.comment}
              onChange={(e) => handleChange('comment')(e)}
              rows={4}
              placeholder="Enter..."
              className="comment-textarea"
            />
          </div>

        </div>
      )}
    </Dialog>
  );
};

AddEditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  baseURL: PropTypes.string.isRequired,
  headers: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
  mode: PropTypes.oneOf(['add', 'edit']),
  initialData: PropTypes.object,
};

AddEditDialog.defaultProps = {
  mode: 'add',
  initialData: {},
  onSuccess: null,
};

export default AddEditDialog;
